import { useNavigation } from "@react-navigation/native";
import { Calendar, Link } from "@tamagui/lucide-icons";
import type { Timestamp } from "firebase/firestore";
import React, { useMemo } from "react";
import { XStack, type XStackProps, YStack } from "tamagui";
import { getDayJs } from "../../../core/dayJs";
import { Permission } from "../../../permissions/types";
import { InfoRow, type InfoRowItemProps } from "../../../ui/InfoRow";
import { Label } from "../../../ui/Label";
import { Subtext } from "../../../ui/Subtext";
import { AvatarSize, UserPropsAvatar } from "../../../ui/UserAvatar";
import type { BusinessStaticUserInfoWithUserUid } from "../../plan/BusinessPlanModel";
import type { BusinessFeedItem } from "../BusinessFeedItem";
import { useDisplayNameInfo } from "../utils";

export const FeedItemAuthorUI = ({
  businessUid,
  feedItem,
  author,
  dateTs,
  ...stackProps
}: {
  businessUid: string;
  feedItem: BusinessFeedItem;
  author?: BusinessStaticUserInfoWithUserUid;
  dateTs?: Timestamp;
} & XStackProps) => {
  const navigation = useNavigation();
  const feedUid = feedItem.uid;

  const getDisplayTag = useDisplayNameInfo();

  const infoRowItems = useMemo<InfoRowItemProps[]>(() => {
    const items: InfoRowItemProps[] = [];

    const displayTag = getDisplayTag(author);

    if (displayTag) {
      items.push(displayTag);
    }

    if (dateTs) {
      items.push({
        icon: Calendar,
        text: getDayJs(dateTs),
      });
    }

    items.push({
      text: (
        <Subtext
          onPress={(e) => {
            e.stopPropagation();
            navigation.navigate("BusinessFeedItem", {
              businessUid,
              itemUid: feedUid,
            });
          }}
        >
          <Link color={"$muted"} size={"$size.small"} />
        </Subtext>
      ),
    });

    if (feedItem.data.edited) {
      items.push({ text: "Edited" });
    }

    return items;
  }, [
    getDisplayTag,
    navigation,
    author,
    dateTs,
    feedUid,
    businessUid,
    feedItem.data.edited,
  ]);

  if (!author) {
    return null;
  }

  return (
    <XStack
      gap={"$space.medium"}
      alignItems={"center"}
      paddingHorizontal={"$larger"}
      paddingVertical={"$medium"}
      borderBottomColor={"$divider"}
      borderBottomWidth={1}
      cursor={"pointer"}
      onPress={(e) => {
        e.stopPropagation();
        feedItem.businessPlan.setShowUserInfo(author.userUid);
      }}
      {...stackProps}
    >
      <UserPropsAvatar
        {...author}
        size={AvatarSize.Medium}
        showBorder={feedItem.businessPlan.checkPermission(
          Permission.AvatarShowExtraBorder,
          author.userUid,
        )}
      />
      <YStack gap={"$small"}>
        <Label>{author.displayName}</Label>

        <InfoRow items={infoRowItems} />
      </YStack>
    </XStack>
  );
};
